import {
  createDefaultBoard,
  defaultTechDeckForFaction,
  defaultUnitDeckForFaction,
  TRI_ANGER_VERSION,
} from "./data";
import type {
  BoardTile,
  FactionId,
  GameState,
  InitGameConfig,
  PlayerState,
} from "./types";
import { cellKey } from "./utils";

function createPlayer(
  config: InitGameConfig["players"][number],
  fallbackFaction: FactionId,
  startingGold: number,
): PlayerState {
  const faction = config.faction ?? fallbackFaction;
  return {
    id: config.id,
    name: config.name?.trim() || config.id,
    faction,
    gold: startingGold,
    techCoins: 0,
    unitDeck: config.unitDeck ?? defaultUnitDeckForFaction(faction),
    techDeck: config.techDeck ?? defaultTechDeckForFaction(faction),
    ownedTech: [],
  };
}

function initialControlByCell(
  board: readonly BoardTile[],
): Readonly<Record<string, string>> {
  const entries = board
    .filter((tile) => tile.kind === "home" && tile.homeOwnerId)
    .map((tile) => [cellKey(tile.position), tile.homeOwnerId ?? ""] as const);
  return Object.fromEntries(entries);
}

export function initGame(config: InitGameConfig): GameState {
  const [playerOneConfig, playerTwoConfig] = config.players;
  const firstPlayerId = config.firstPlayerId ?? playerOneConfig.id;
  const playerOne = createPlayer(
    playerOneConfig,
    playerOneConfig.faction ?? "ECSU",
    firstPlayerId === playerOneConfig.id ? 5 : 8,
  );
  const playerTwo = createPlayer(
    playerTwoConfig,
    playerTwoConfig.faction ?? "GO",
    firstPlayerId === playerTwoConfig.id ? 5 : 8,
  );
  const board = createDefaultBoard(playerOne.id, playerTwo.id);

  return {
    gameId: config.gameId ?? `tri-anger-${config.seed ?? Date.now()}`,
    version: TRI_ANGER_VERSION,
    board,
    players: [playerOne, playerTwo],
    currentPlayerId: firstPlayerId,
    turn: 1,
    phase: "MOVE",
    units: [],
    constructionByCell: {},
    controlByCell: initialControlByCell(board),
    turnFlags: { boughtTechCoinPlayerIds: [] },
    combatLog: [],
    log: [
      {
        turn: 1,
        phase: "MOVE",
        message: "Tri-Anger game initialized.",
      },
    ],
    nextUnitSequence: 1,
  };
}
